import { all, put, select, takeEvery } from "redux-saga/effects";
import {
    createMultiStepReaction,
    createReaction,
    ILibraryMultiStepReaction,
    ILibraryReaction,
    updateMultiStepReaction,
    updateReaction,
} from "../actions/library";
import {
    setAnalyzerMode,
    setShownMultiStepReaction,
    setShownReaction,
} from "../actions/reactions";
import type { RootState } from "../store";
import type { ReduxActionMeta } from "../types/ActionMeta";
import {
    buildReactionLookup,
    buildRouteLookup,
    resolveRouteForAnalyzer,
} from "../../utils/reactionRoute";
import type { AnalyzerSyncIntent } from "../../domain/drafts/persistence/types";

type RsaaSuccessAction<P> = {
    type: string;
    payload: P;
    meta?: ReduxActionMeta;
};

const shouldSyncAnalyzer = (
    meta: RsaaSuccessAction<unknown>["meta"],
    entity: AnalyzerSyncIntent["entity"]
): boolean => meta?.syncToAnalyzerOnSuccess === true && meta.entity === entity;

export function* syncAnalyzerReactionOnLibrarySaveSuccess(
    action: RsaaSuccessAction<ILibraryReaction>
) {
    if (!shouldSyncAnalyzer(action.meta, "reaction")) {
        return;
    }

    yield put(setShownReaction(action.payload));
    yield put(setAnalyzerMode("single"));
}

export function* syncAnalyzerRouteOnLibrarySaveSuccess(
    action: RsaaSuccessAction<ILibraryMultiStepReaction>
) {
    if (!shouldSyncAnalyzer(action.meta, "route")) {
        return;
    }

    const libraryReactions: ILibraryReaction[] = yield select(
        (state: RootState) => state.library.reactions || []
    );
    const reactionLookup = buildReactionLookup(libraryReactions);
    const libraryRoutes: ILibraryMultiStepReaction[] = yield select(
        (state: RootState) => state.library.multiStepReactions || []
    );
    const routeLookup = buildRouteLookup(libraryRoutes);
    const resolvedRoute = resolveRouteForAnalyzer(
        action.payload,
        reactionLookup,
        routeLookup
    );

    yield put(setShownMultiStepReaction(resolvedRoute));
    yield put(setAnalyzerMode("multi-step"));
}

export default function* reactionAnalyzerPersistenceSaga() {
    yield all([
        takeEvery(
            createReaction.type_success,
            syncAnalyzerReactionOnLibrarySaveSuccess
        ),
        takeEvery(
            updateReaction.type_success,
            syncAnalyzerReactionOnLibrarySaveSuccess
        ),
        takeEvery(
            createMultiStepReaction.type_success,
            syncAnalyzerRouteOnLibrarySaveSuccess
        ),
        takeEvery(
            updateMultiStepReaction.type_success,
            syncAnalyzerRouteOnLibrarySaveSuccess
        ),
    ]);
}
